/**
 * Work intents: what is waiting on a person, approvals and what waiting
 * costs, the most expensive open job, the agent-drafted work orders, the PMs
 * due in a window, and what is waiting in a workflow module.
 */

import { BY_ID, IDS, MINES } from '../../../data/core.ts';
import { groupNum, fmt$ } from '../../oem-engine.ts';
import { ALERTS } from '../../seed/alerts.ts';
import { MODULES, MODULE_BY_ID } from '../../seed/modules.ts';
import { loadOemDomain } from '../../useOemData.ts';
import { firstName, loadJobs, route, scopeLabel } from '../answer-kit.ts';
import { TOOLS, loadWorkOrders } from '../tools.ts';
import { cues, type IntentDef } from './types.ts';

// ---------------------------------------------------------------------------
// jobs_waiting — demo L4063
// ---------------------------------------------------------------------------

export const jobsWaiting: IntentDef = {
  id: 'jobs_waiting',
  family: 'work',
  priority: 86,
  examples: ['Which jobs are waiting on a person?', 'What needs a decision today?'],
  score: cues({
    any: [/waiting (on|for) a (person|human)|needs? a decision|awaiting approval|waiting on a human/],
    not: [/what happens if i wait|am i approving/],
    base: 4,
  }),
  async answer({ kit }) {
    kit.ran('jobs_awaiting_decision');
    const J = (await loadJobs()).filter((j) => /Approve|approval|Planner/i.test(j.next));
    const markdown =
      `**${J.length} jobs are waiting on a person**: ` +
      `${J.map((j) => `**${j.machine || j.id}** — ${j.next} (${j.owner})`).join('; ')}. ` +
      `Each is a draft in Approvals; nothing has touched a machine, a schedule or the order book.`;
    return kit.done(markdown, ['job board', 'work_orders.csv', 'order_history.csv'], {
      navigate: route('approvals'),
    });
  },
};

// ---------------------------------------------------------------------------
// approvals_waiting — demo L4070
// ---------------------------------------------------------------------------

export const approvalsWaiting: IntentDef = {
  id: 'approvals_waiting',
  family: 'work',
  priority: 72,
  examples: ['What am I approving on T-09 and what happens if I wait?', 'What happens if I wait on T-09?'],
  score: cues({
    any: [/approv/, /decision/, /waiting on/, /sign off/, /what happens if (i|we) wait|if (i|we) wait/],
    not: [/without approval/],
    penalty: [[/agents? (can|do)|autonomy|\bL[1-4]\b/, 3]],
  }),
  async answer({ kit, E }) {
    const mid = E.machine;
    const mine = mid ? null : E.mine;
    const [workOrders, parts] = await Promise.all([loadWorkOrders(), loadOemDomain('parts')]);
    const drafts = workOrders.filter(
      (w) =>
        w.status.startsWith('Draft') && (!mid || w.machine_id === mid) && (!mine || w.mine === mine),
    );
    const al = ALERTS.filter(
      (a) => (!mid || a.machine === mid) && (!mine || BY_ID[a.machine].mine === mine),
    );
    const so = parts.orders.filter((o) => /agent/i.test(o.status) && (!mid || o.machine_id === mid));
    await kit.run(TOOLS.fleet_overview, {});

    const scope = scopeLabel({ ...E, mine: mine ?? undefined });
    const restriction =
      (!mid || mid === IDS.T22) && (!mine || mine === 'MI')
        ? `\n- **RESTRICT-${IDS.T22}** — empty-haul only until WO-26-11912 closes (Safety & compliance agent)`
        : '';
    const markdown =
      `**Waiting on a person${scope}**\n\n` +
      drafts
        .map(
          (w) =>
            `- **${w.wo_id}** ${w.machine_id} (${w.mine}) — ${w.description.split('.')[0]} · ` +
            `A$${groupNum(w.total_direct_cost_aud)} direct · ${w.crew}`,
        )
        .join('\n') +
      (so.length
        ? `\n${so
            .map((o) => `- **${o.order_no}** ${o.machine_id} — ${o.status} · ${o.part_no} × ${o.qty || 1}`)
            .join('\n')}`
        : '') +
      restriction +
      (al.length
        ? `\n\n**If you wait:** ${al
            .map(
              (a) =>
                `${a.machine} — ${a.window}, ${Math.round(a.conf * 100)}% model confidence. ${
                  a.why.split('. ')[0]
                }.`,
            )
            .join(' ')}`
        : '') +
      `\n\nApprove in the Approvals view — nothing touches a machine, a schedule or the order book until then.`;
    if (mid) kit.focus({ machine: mid, mine: BY_ID[mid].mine });
    return kit.done(markdown, ['work_orders', 'alerts', 'orders']);
  },
};

// ---------------------------------------------------------------------------
// most_expensive_job — demo L4065
// ---------------------------------------------------------------------------

export const mostExpensiveJob: IntentDef = {
  id: 'most_expensive_job',
  family: 'work',
  priority: 89,
  examples: ['What is the most expensive open job?'],
  score: cues({ any: [/most expensive (open )?(job|work order|draft)|biggest open (job|work order)/], base: 6 }),
  async answer({ kit }) {
    kit.ran('open_work_orders', { order_by: 'direct cost', limit: 1 });
    const workOrders = await loadWorkOrders();
    const d = workOrders
      .filter((w) => w.status.startsWith('Draft') || w.status.startsWith('Released'))
      .slice()
      .sort((a, b) => b.total_direct_cost_aud - a.total_direct_cost_aud)[0];
    const markdown =
      `The most expensive open draft is **${d.wo_id}** on **${d.machine_id}** at ${MINES[d.mine].name}: ` +
      `${fmt$(d.total_direct_cost_aud)} direct (${d.labour_h} h labour, parts ${d.parts_used || '—'}). ` +
      `It is at the Plan stage awaiting ${firstName(MINES[d.mine].planner)}.`;
    kit.focus({ machine: d.machine_id, component: d.component, mine: d.mine });
    return kit.done(markdown, ['work_orders.csv'], { navigate: route('approvals') });
  },
};

// ---------------------------------------------------------------------------
// agent_drafted_wos — new
// ---------------------------------------------------------------------------

export const agentDraftedWos: IntentDef = {
  id: 'agent_drafted_wos',
  family: 'work',
  priority: 85,
  examples: ['Which open work orders are agent-drafted?'],
  score: cues({
    any: [/agent[- ]drafted|drafted by (an |the )?agents?|agent drafts|draft(ed)? work orders|work orders? .*draft/],
    base: 6,
  }),
  async answer({ kit, E }) {
    kit.ran('open_work_orders', { status: 'Draft' });
    const workOrders = await loadWorkOrders();
    const drafts = workOrders
      .filter((w) => w.status.startsWith('Draft') && (!E.mine || w.mine === E.mine))
      .sort((a, b) => b.total_direct_cost_aud - a.total_direct_cost_aud);
    const total = drafts.reduce((a, w) => a + w.total_direct_cost_aud, 0);
    const markdown =
      `**${drafts.length} open work orders are agent-drafted**${E.mine ? ` at ${MINES[E.mine].name}` : ''} — ` +
      `${fmt$(total)} direct between them, every one waiting on a person.\n\n` +
      `| WO | Machine | Component | Direct | Crew |\n|---|---|---|---|---|\n` +
      drafts
        .slice(0, 12)
        .map((w) => `| ${w.wo_id} | ${w.machine_id} (${w.mine}) | ${w.component} | ${fmt$(w.total_direct_cost_aud)} | ${w.crew} |`)
        .join('\n') +
      `\n\nThe Work Order agent drafts from an alert or a close-out; nothing is released until approved.`;
    return kit.done(markdown, ['work_orders.csv'], { navigate: route('approvals') });
  },
};

// ---------------------------------------------------------------------------
// pm_due — new: "what is due at Mount Isolde in the next 7 days?"
// ---------------------------------------------------------------------------

export const pmDue: IntentDef = {
  id: 'pm_due',
  family: 'work',
  priority: 81,
  examples: ['What is due at Mount Isolde in the next 7 days?', 'Which PMs are due this week?'],
  score: cues({
    any: [
      /\bdue\b/,
      /\bpms?\b.*(coming|scheduled|next)|preventives? (scheduled|coming|due)|scheduled (service|maintenance|pms?)/,
      /services? (coming up|due)/,
    ],
    not: [/overdue|due to\b/],
    bonus: (E) => (E.days ? 1 : 0),
    base: 5,
  }),
  async answer({ kit, E }) {
    const days = E.days ?? 7;
    const r = await kit.run(TOOLS.pm_due, {
      days,
      mine: E.machine ? null : (E.mine ?? null),
      machine_id: E.machine ?? null,
      cls: E.cls ? E.cls.classes.join(',') : null,
    });
    const scope = scopeLabel(E) + (E.cls ? ` · ${E.cls.label}` : '');
    const byDay = new Map<string, number>();
    r.rows.forEach((x) => byDay.set(x.date, (byDay.get(x.date) ?? 0) + 1));
    const markdown =
      `**${r.count} preventives due${scope}** in the ${E.windowLabel ?? `next ${days} days`} — ${r.labour_h} labour hours.\n\n` +
      (r.rows.length
        ? `| Date | Machine | PM | Crew | Labour h |\n|---|---|---|---|---|\n` +
          r.rows
            .slice(0, 10)
            .map((x) => `| ${x.date} | ${x.machine} (${x.mine}) | ${x.pm_code ?? '—'} | ${x.crew} | ${x.labour_h} |`)
            .join('\n') +
          (r.rows.length > 10 ? `\n\n…and ${r.rows.length - 10} more.` : '')
        : 'Nothing scheduled in that window.') +
      `\n\nBusiest day: ${[...byDay.entries()].sort((a, b) => b[1] - a[1])[0]?.join(' — ') ?? '—'} PM${
        ([...byDay.values()].sort((a, b) => b - a)[0] ?? 0) === 1 ? '' : 's'
      }.`;
    kit.remember({ days });
    if (E.mine) kit.focus({ mine: E.mine });
    return kit.done(markdown, ['pm_due', 'SAP PM work_orders']);
  },
};

// ---------------------------------------------------------------------------
// module_waiting — new: "what is waiting in this module?"
// ---------------------------------------------------------------------------

export const moduleWaiting: IntentDef = {
  id: 'module_waiting',
  family: 'work',
  priority: 90,
  examples: ['What is waiting in this module?'],
  needs: ['module'],
  score: cues({
    any: [/(waiting|open|sitting|stuck|in progress|queued) (in|at) (this|the) module|what('s| is) (in|waiting in) (this|the) module/],
    base: 6,
  }),
  async answer({ kit, E }) {
    const mod = MODULE_BY_ID[E.module!];
    kit.ran('open_jobs_by_module', { module: mod.id });
    const J = (await loadJobs()).filter((j) => j.stage === mod.id);
    const waiting = J.filter((j) => /Approve|approval|Planner/i.test(j.next));
    const markdown =
      `**${String(mod.n).padStart(2, '0')} ${mod.name}** — ${J.length} open job${J.length === 1 ? '' : 's'}, ` +
      `${waiting.length} waiting on a person.\n\n` +
      (J.length
        ? J.slice(0, 8)
            .map((j) => `- **${j.machine || j.id}** ${j.title} — next: ${j.next} (${j.owner})`)
            .join('\n')
        : '- Nothing open at this module right now.') +
      `\n\nRun by ${mod.agents.join(' and ')}.`;
    return kit.done(markdown, ['job board', 'work_orders.csv'], {
      navigate: route('module', mod.id),
    });
  },
};

/** Module names for the fallback help. */
export const MODULE_NAMES = MODULES.map((m) => m.name);
